// src/pages/DebugPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/apiClient';
import Layout from '../components/layout/Layout';

const DebugPage = () => {
  const [logs, setLogs] = useState([]);
  const [turnstileStatus, setTurnstileStatus] = useState('Loading...');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileTest, setTurnstileTest] = useState(null);
  const [siteKey, setSiteKey] = useState('');
  const [apiStatus, setApiStatus] = useState({});
  
  const logToServer = useCallback(async (message, data = {}) => {
    try {
      const logEntry = {
        message,
        data,
        timestamp: new Date().toISOString(),
        source: 'debug-page'
      };
      
      // Add to local logs immediately
      setLogs(prev => [...prev, logEntry]);
      
      // Send to server
      await apiClient.post('/debug/log', logEntry);
    } catch (error) {
      console.error('Failed to log to server:', error);
      // Still add to local logs
      setLogs(prev => [...prev, {
        message: `Failed to log to server: ${message}`,
        data: { originalData: data, error: error.message },
        timestamp: new Date().toISOString(),
        level: 'error'
      }]);
    }
  }, []);
  
  const testApiEndpoints = useCallback(async () => {
    const endpoints = [
      { name: 'Config API', url: '/config' },
      { name: 'Debug Log API', url: '/debug/log', method: 'post', data: { message: 'Test log', data: { test: true } } }
    ];
    
    const results = {};
    
    for (const endpoint of endpoints) {
      try {
        const method = endpoint.method || 'get';
        const response = await apiClient[method](endpoint.url, endpoint.data);
        results[endpoint.name] = { status: 'Success', data: response.data };
        logToServer(`API test: ${endpoint.name}`, { success: true, data: response.data });
      } catch (error) {
        results[endpoint.name] = { 
          status: 'Error', 
          error: error.message,
          response: error.response?.data
        };
        logToServer(`API test: ${endpoint.name}`, { 
          success: false, 
          error: error.message,
          response: error.response?.data
        });
      }
    }
    
    setApiStatus(results);
  }, [logToServer]);
  
  const renderTurnstileWidget = useCallback(() => {
    if (!window.turnstile) {
      setTurnstileStatus('Error: turnstile object not found in window');
      logToServer('Turnstile object not found in window');
      return;
    }
    
    try {
      const container = document.getElementById('turnstile-widget');
      if (!container) {
        setTurnstileStatus('Error: Container not found');
        return;
      }
      
      // Clear any existing children
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      
      setTurnstileStatus('Rendering widget...');
      
      window.turnstile.render(container, {
        sitekey: siteKey,
        callback: function(token) {
          setTurnstileToken(token);
          setTurnstileStatus('Token received');
          logToServer('Turnstile token received', { tokenPrefix: token.substring(0, 10) + '...' });
        },
        'error-callback': function(error) {
          setTurnstileStatus(`Error: ${error}`);
          logToServer('Turnstile error', { error });
        }
      });
    } catch (error) {
      setTurnstileStatus(`Error rendering widget: ${error.message}`);
      logToServer('Error rendering Turnstile widget', { error: error.message });
    }
  }, [logToServer, siteKey]);
  
  const loadTurnstileScript = useCallback(() => {
    // Remove any existing script to avoid duplicates
    const existingScript = document.querySelector('script[src*="turnstile"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    setTurnstileStatus('Loading script...');
    logToServer('Loading Turnstile script');
    
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => {
      setTurnstileStatus('Script loaded, rendering widget...');
      logToServer('Turnstile script loaded');
      
      // Give some time for the turnstile object to initialize
      setTimeout(renderTurnstileWidget, 500);
    };
    script.onerror = (e) => {
      setTurnstileStatus('Failed to load script');
      logToServer('Turnstile script failed to load', { error: e.type });
    };
    document.body.appendChild(script);
  }, [logToServer, renderTurnstileWidget]);
  
  useEffect(() => {
    // Get site key from env
    setSiteKey(process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY || 'Not found in .env');
    
    // Log page load
    logToServer('Debug page loaded', { 
      url: window.location.href,
      userAgent: navigator.userAgent
    });
    
    // Test API endpoints
    testApiEndpoints();
    
    // Load Turnstile script
    loadTurnstileScript();
  }, [logToServer, testApiEndpoints, loadTurnstileScript]);
  
  const testTurnstileVerification = async () => {
    if (!turnstileToken) {
      alert('No Turnstile token available. Please complete the challenge first.');
      return;
    }
    
    try {
      logToServer('Testing Turnstile verification', { tokenPrefix: turnstileToken.substring(0, 10) + '...' });
      const response = await apiClient.post('/debug/turnstile/test', { token: turnstileToken });
      setTurnstileTest(response.data);
      logToServer('Turnstile test response', response.data);
    } catch (error) {
      setTurnstileTest({ 
        status: 'error',
        error: error.message,
        response: error.response?.data
      });
      logToServer('Turnstile test error', { 
        error: error.message,
        response: error.response?.data
      });
    }
  };
  
  return (
    <Layout>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Debug Tools</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="border rounded-lg p-4">
            <h2 className="text-xl font-semibold mb-4">Cloudflare Turnstile</h2>
            <div className="mb-4">
              <p className="mb-2">Site Key: <code className="bg-gray-100 p-1 rounded">{siteKey}</code></p>
              <p className="mb-2">Status: <span className={turnstileStatus.includes('Error') ? 'text-red-600' : turnstileStatus === 'Token received' ? 'text-green-600' : 'text-yellow-600'}>{turnstileStatus}</span></p>
              
              {turnstileToken && (
                <div className="mb-2">
                  <p className="font-medium">Token:</p>
                  <div className="bg-gray-100 p-2 rounded overflow-x-auto">
                    <code className="text-xs break-all">{turnstileToken}</code>
                  </div>
                </div>
              )}
            </div>
            
            <div className="mb-4">
              <div id="turnstile-widget" className="mb-4"></div>
              
              <div className="flex space-x-2">
                <button 
                  onClick={loadTurnstileScript}
                  className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                >
                  Reload Widget
                </button>
                
                <button 
                  onClick={testTurnstileVerification}
                  className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
                  disabled={!turnstileToken}
                >
                  Test Verification
                </button>
              </div>
            </div>
            
            {turnstileTest && (
              <div className="mt-4">
                <h3 className="font-medium mb-2">Verification Test Result:</h3>
                <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                  {JSON.stringify(turnstileTest, null, 2)}
                </pre>
              </div>
            )}
          </div>
          
          <div className="border rounded-lg p-4">
            <h2 className="text-xl font-semibold mb-4">API Status</h2>
            
            {Object.keys(apiStatus).length === 0 ? (
              <p className="text-gray-500">Testing API endpoints...</p>
            ) : (
              <div>
                {Object.entries(apiStatus).map(([name, result]) => (
                  <div key={name} className="mb-4">
                    <h3 className="font-medium">{name}: <span className={result.status === 'Success' ? 'text-green-600' : 'text-red-600'}>{result.status}</span></h3>
                    <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto mt-1">
                      {JSON.stringify(result.status === 'Success' ? result.data : result.error, null, 2)}
                    </pre>
                  </div>
                ))}
                
                <button 
                  onClick={testApiEndpoints}
                  className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 mt-2"
                >
                  Test Again
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="border rounded-lg p-4">
          <h2 className="text-xl font-semibold mb-4">Debug Logs</h2>
          
          <div className="bg-gray-100 p-2 rounded overflow-y-auto max-h-96">
            {logs.length === 0 ? (
              <p className="text-gray-500">No logs yet</p>
            ) : (
              logs.map((log, index) => (
                <div key={index} className="mb-2 pb-2 border-b border-gray-200">
                  <div className="flex items-start">
                    <span className="text-gray-500 text-xs">{new Date(log.timestamp).toLocaleTimeString()}</span>
                    <span className="ml-2 font-medium">{log.message}</span>
                  </div>
                  {log.data && (
                    <pre className="text-xs mt-1 text-gray-700">
                      {JSON.stringify(log.data, null, 2)}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DebugPage;